import { useMemo, useState, useEffect } from 'react';
import {
    Box,
    Tabs,
    Tab,
    Button,
    Paper,
    Card,
    CardContent,
    Typography,
    TextField,
    Select,
    MenuItem,
    FormControl,
    FormControlLabel,
    Radio,
    Checkbox,
    Switch,
    IconButton,
    InputLabel,
    RadioGroup,
    Grid,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import PublishIcon from '@mui/icons-material/Publish';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import UploadIcon from '@mui/icons-material/Upload';
import GetAppIcon from '@mui/icons-material/GetApp';

import Styles from './CreateQuizAssessment.module.scss'

export const QuizAssessmentForm = () => {
    const [activeTab, setActiveTab] = useState(0);
    const [questions, setQuestions] = useState([]);
    const [settings, setSettings] = useState({
        timedQuiz: false,
        quizTime: '00:00:00',
        shuffleQuestions: true,
        shuffleOptions: false,
        defaultPoints: 1,
        attempts: 1
    });
    const [timeLimitError, setTimeLimitError] = useState("");
    const [isValid, setIsValid] = useState(false);
    const [validationMessages, setValidationMessages] = useState([]);

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const getEmptyQuestionTemplate = (type) => {
        const baseTemplate = {
            id: Date.now(),
            type,
            question: '',
            randomize: false,
            points: settings.defaultPoints,
            includeImage: false,
            required: true,
        };

        switch (type) {
            case 'MCQ':
            case 'MSQ':
                return {
                    ...baseTemplate,
                    options: ['', '', '', ''],
                    selectedAnswers: []
                };
            case 'SHORT_ANSWER':
                return {
                    ...baseTemplate,
                    maxLength: 500,
                    answer: '' // Model answer for grading
                };
            case 'RADIO_GRID':
                return {
                    ...baseTemplate,
                    rows: ['', ''],
                    columns: ['', ''],
                    answers: {} // Format: {rowIndex: columnIndex}
                };
            case 'CHECKBOX_GRID':
                return {
                    ...baseTemplate,
                    rows: ['', ''],
                    columns: ['', ''],
                    answers: {} // Format: {rowIndex: [columnIndices]}
                };
            default:
                return baseTemplate;
        }
    };

    const addQuestion = () => {
        setQuestions([...questions, getEmptyQuestionTemplate('MCQ')]);
    };

    const deleteQuestion = (id) => {
        setQuestions(questions.filter(q => q.id !== id));
    };

    const updateQuestion = (id, field, value) => {
        setQuestions(questions.map(q =>
            q.id === id ? { ...q, [field]: value } : q
        ));
    };

    const updateOption = (questionId, optionIndex, value) => {
        setQuestions(questions.map(q => {
            if (q.id === questionId) {
                const newOptions = [...q.options];
                newOptions[optionIndex] = value;
                return { ...q, options: newOptions };
            }
            return q;
        }));
    };

    const updateGridItem = (questionId, type, index, value, isRow = true) => {
        setQuestions(questions.map(q => {
            if (q.id === questionId) {
                const field = isRow ? 'rows' : 'columns';
                const newItems = [...q[field]];
                newItems[index] = value;
                return { ...q, [field]: newItems };
            }
            return q;
        }));
    };

    const addGridItem = (questionId, isRow = true) => {
        setQuestions(questions.map(q => {
            if (q.id === questionId) {
                const field = isRow ? 'rows' : 'columns';
                return { ...q, [field]: [...q[field], ''] };
            }
            return q;
        }));
    };

    const deleteGridItem = (questionId, index, isRow = true) => {
        setQuestions(questions.map(q => {
            if (q.id === questionId) {
                const field = isRow ? 'rows' : 'columns';
                // Ensure we keep at least 2 rows/columns
                if (q[field].length <= 2) return q;

                const newItems = [...q[field]];
                newItems.splice(index, 1);

                // Update answers when deleting rows/columns
                let newAnswers = { ...q.answers };
                if (isRow) {
                    // Remove answers for deleted row
                    delete newAnswers[index];
                    // Shift answers for rows after the deleted row
                    for (let i = index; i < q[field].length - 1; i++) {
                        newAnswers[i] = newAnswers[i + 1];
                        delete newAnswers[i + 1];
                    }
                } else {
                    // Update answers when column is deleted
                    Object.keys(newAnswers).forEach(rowIdx => {
                        if (q.type === 'RADIO_GRID') {
                            if (newAnswers[rowIdx] === index) {
                                delete newAnswers[rowIdx];
                            } else if (newAnswers[rowIdx] > index) {
                                newAnswers[rowIdx]--;
                            }
                        } else { // CHECKBOX_GRID
                            if (Array.isArray(newAnswers[rowIdx])) {
                                newAnswers[rowIdx] = newAnswers[rowIdx]
                                    .filter(colIdx => colIdx !== index)
                                    .map(colIdx => colIdx > index ? colIdx - 1 : colIdx);
                            }
                        }
                    });
                }

                return {
                    ...q,
                    [field]: newItems,
                    answers: newAnswers
                };
            }
            return q;
        }));
    };

    const handleTypeChange = (questionId, newType) => {
        setQuestions(questions.map(q => {
            if (q.id === questionId) {
                return getEmptyQuestionTemplate(newType);
            }
            return q;
        }));
    };

    const handleAnswerSelection = (questionId, rowIndex, columnIndex, type) => {
        setQuestions(questions.map(q => {
            if (q.id === questionId) {
                if (type === 'RADIO_GRID') {
                    return {
                        ...q,
                        answers: { ...q.answers, [rowIndex]: columnIndex }
                    };
                } else if (type === 'CHECKBOX_GRID') {
                    const currentAnswers = q.answers[rowIndex] || [];
                    const newAnswers = currentAnswers.includes(columnIndex)
                        ? currentAnswers.filter(i => i !== columnIndex)
                        : [...currentAnswers, columnIndex];
                    return {
                        ...q,
                        answers: { ...q.answers, [rowIndex]: newAnswers }
                    };
                }
                // Handle MCQ/MSQ as before
                if (type === 'MCQ') {
                    return { ...q, selectedAnswers: [rowIndex] };
                } else {
                    const newSelectedAnswers = q.selectedAnswers.includes(rowIndex)
                        ? q.selectedAnswers.filter(idx => idx !== rowIndex)
                        : [...q.selectedAnswers, rowIndex];
                    return { ...q, selectedAnswers: newSelectedAnswers };
                }
            }
            return q;
        }));
    };

    const handlePointsChange = (questionId, value) => {
        const points = Math.max(1, Math.min(10, Number(value)));
        setQuestions(questions.map(q =>
            q.id === questionId ? { ...q, points } : q
        ));
    };

    const handleSettingChange = (field, value) => {
        if (field === 'timeLimit') {
            const timeFormat = /^([0-1]\d|2[0-3]):([0-5]\d):([0-5]\d)$/;
            if (!timeFormat.test(value)) {
                setTimeLimitError("Invalid format. Use HH:mm:ss");
            } else {
                setTimeLimitError("");
            }
        }
        setSettings(prev => ({ ...prev, [field]: value }));

        if (field === 'defaultPoints') {
            const newDefaultPoints = Math.max(1, Math.min(10, Number(value)));
            setQuestions(questions.map(q =>
                q.points === settings.defaultPoints ? { ...q, points: newDefaultPoints } : q
            ));
        }
    };

    const renderQuestionContent = (q) => {
        switch (q.type) {
            case 'MCQ':
            case 'MSQ':
                return (
                    <Box sx={{ mt: 2 }}>
                        {q.type === 'MCQ' ? (
                            <RadioGroup
                                value={q.selectedAnswers[0] ?? ''}
                                onChange={(e) => handleAnswerSelection(q.id, Number(e.target.value), null, 'MCQ')}
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    marginBottom: 0
                                }}
                            >
                                {q.options.map((option, optionIndex) => (
                                    <Box key={optionIndex} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                        <FormControlLabel
                                            value={optionIndex}
                                            control={<Radio />}
                                            label={
                                                <TextField
                                                    required
                                                    fullWidth
                                                    label={`Option ${optionIndex + 1}`}
                                                    value={option}
                                                    onChange={(e) => updateOption(q.id, optionIndex, e.target.value)}
                                                    size="small"
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                            }
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center'
                                            }}
                                        />
                                    </Box>
                                ))}
                            </RadioGroup>
                        ) : (
                            q.options.map((option, optionIndex) => (
                                <Box key={optionIndex} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={q.selectedAnswers.includes(optionIndex)}
                                                onChange={() => handleAnswerSelection(q.id, optionIndex)}
                                            />
                                        }
                                        label={
                                            <TextField
                                                required
                                                fullWidth
                                                label={`Option ${optionIndex + 1}`}
                                                value={option}
                                                onChange={(e) => updateOption(q.id, optionIndex, e.target.value)}
                                                size="small"
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        }
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center'
                                        }}
                                    />
                                </Box>
                            ))
                        )}
                        <Box sx={{ display: 'flex', gap: 3, p: 1 }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        size="small"
                                        checked={q.randomize}
                                        onChange={(e) => updateQuestion(q.id, 'randomize', e.target.checked)}
                                    />
                                }
                                label="Randomize Options"
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        size="small"
                                        checked={q.includeImage}
                                        onChange={(e) => updateQuestion(q.id, 'includeImage', e.target.checked)}
                                    />
                                }
                                label="Include Image"
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        size="small"
                                        checked={q.required}
                                        onChange={(e) => updateQuestion(q.id, 'required', e.target.checked)}
                                    />
                                }
                                label="Required"
                            />
                        </Box>
                        {q.includeImage && (
                            <Box sx={{ mb: 2 }}>
                                <input
                                    accept="image/*"
                                    type="file"
                                    onChange={(e) => {
                                        // Handle image upload here
                                        // For now, just log the file
                                        console.log('Image selected:', e.target.files[0]);
                                    }}
                                    style={{ display: 'none' }}
                                    id={`image-upload-${q.id}`}
                                />
                                <label htmlFor={`image-upload-${q.id}`}>
                                    <Button
                                        variant="outlined"
                                        component="span"
                                        startIcon={<UploadIcon />}
                                        size="small"
                                    >
                                        Upload Image
                                    </Button>
                                </label>
                            </Box>
                        )}
                    </Box>
                );

            case 'SHORT_ANSWER':
                return (
                    <Box sx={{ mt: 2 }}>
                        <TextField
                            required
                            fullWidth
                            label="Answer"
                            value={q.answer}
                            onChange={(e) => updateQuestion(q.id, 'answer', e.target.value)}
                            multiline
                            rows={3}
                            margin="normal"
                        />
                        <TextField
                            type="number"
                            label="Maximum Length"
                            value={q.maxLength}
                            onChange={(e) => updateQuestion(q.id, 'maxLength', Math.max(1, parseInt(e.target.value)))}
                            size="small"
                            sx={{ mt: 2 }}
                        />
                        <Box sx={{ display: 'flex', gap: 3, p: 1 }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        size="small"
                                        checked={q.randomize}
                                        onChange={(e) => updateQuestion(q.id, 'randomize', e.target.checked)}
                                    />
                                }
                                label="Randomize Options"
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        size="small"
                                        checked={q.includeImage}
                                        onChange={(e) => updateQuestion(q.id, 'includeImage', e.target.checked)}
                                    />
                                }
                                label="Include Image"
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        size="small"
                                        checked={q.required}
                                        onChange={(e) => updateQuestion(q.id, 'required', e.target.checked)}
                                    />
                                }
                                label="Required"
                            />
                        </Box>
                        {q.includeImage && (
                            <Box sx={{ mb: 2 }}>
                                <input
                                    accept="image/*"
                                    type="file"
                                    onChange={(e) => {
                                        // Handle image upload here
                                        // For now, just log the file
                                        console.log('Image selected:', e.target.files[0]);
                                    }}
                                    style={{ display: 'none' }}
                                    id={`image-upload-${q.id}`}
                                />
                                <label htmlFor={`image-upload-${q.id}`}>
                                    <Button
                                        variant="outlined"
                                        component="span"
                                        startIcon={<UploadIcon />}
                                        size="small"
                                    >
                                        Upload Image
                                    </Button>
                                </label>
                            </Box>
                        )}
                    </Box>
                );

            case 'RADIO_GRID':
            case 'CHECKBOX_GRID':
                return (
                    <Box sx={{ mt: 2 }}>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell></TableCell>
                                        {q.columns.map((col, colIndex) => (
                                            <TableCell key={colIndex} align="center">
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <TextField
                                                        required
                                                        size="small"
                                                        value={col}
                                                        onChange={(e) => updateGridItem(q.id, q.type, colIndex, e.target.value, false)}
                                                        placeholder={`Column ${colIndex + 1}`}
                                                    />
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => deleteGridItem(q.id, colIndex, false)}
                                                        sx={{ p: 0.5 }}
                                                        disabled={q.columns.length <= 2}
                                                    >
                                                        <CloseIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {q.rows.map((row, rowIndex) => (
                                        <TableRow key={rowIndex}>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <TextField
                                                        required
                                                        size="small"
                                                        value={row}
                                                        onChange={(e) => updateGridItem(q.id, q.type, rowIndex, e.target.value, true)}
                                                        placeholder={`Row ${rowIndex + 1}`}
                                                    />
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => deleteGridItem(q.id, rowIndex, true)}
                                                        sx={{ p: 0.5 }}
                                                        disabled={q.rows.length <= 2}
                                                    >
                                                        <CloseIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            </TableCell>

                                            {/* Handling RADIO_GRID */}
                                            {q.type === 'RADIO_GRID' &&
                                                q.columns.map((_, colIndex) => (
                                                    <TableCell
                                                        key={colIndex}
                                                        align="center"
                                                    >
                                                        <RadioGroup
                                                            name={`radio-group-${rowIndex}`}
                                                            value={q.answers[rowIndex] ?? ""}
                                                            onChange={(e) => handleAnswerSelection(q.id, rowIndex, Number(e.target.value), "RADIO_GRID")}
                                                            sx={{
                                                                display: 'flex',
                                                                justifyContent: 'center',
                                                                alignItems: 'center'
                                                            }}
                                                        >
                                                            <FormControlLabel
                                                                value={colIndex}
                                                                control={<Radio checked={q.answers[rowIndex] === colIndex} />}
                                                                label=""
                                                                sx={{
                                                                    display: 'flex',
                                                                    justifyContent: 'center',
                                                                    alignItems: 'center'
                                                                }}
                                                            />
                                                        </RadioGroup>
                                                    </TableCell>
                                                ))}

                                            {/* Handling CHECKBOX_GRID */}
                                            {q.type === 'CHECKBOX_GRID' &&
                                                q.columns.map((_, colIndex) => (
                                                    <TableCell key={colIndex} align="center">
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    checked={(q.answers[rowIndex] || []).includes(colIndex)}
                                                                    onChange={() => handleAnswerSelection(q.id, rowIndex, colIndex, "CHECKBOX_GRID")}
                                                                />
                                                            }
                                                            label=""
                                                            sx={{
                                                                display: 'flex',
                                                                justifyContent: 'center',
                                                                alignItems: 'center'
                                                            }}
                                                        />
                                                    </TableCell>
                                                ))}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                            <Button variant="outlined" size="small" onClick={() => addGridItem(q.id, true)}>
                                Add Row
                            </Button>
                            <Button variant="outlined" size="small" onClick={() => addGridItem(q.id, false)}>
                                Add Column
                            </Button>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 3, p: 1 }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        size="small"
                                        checked={q.randomize}
                                        onChange={(e) => updateQuestion(q.id, 'randomize', e.target.checked)}
                                    />
                                }
                                label="Randomize Options"
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        size="small"
                                        checked={q.includeImage}
                                        onChange={(e) => updateQuestion(q.id, 'includeImage', e.target.checked)}
                                    />
                                }
                                label="Include Image"
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        size="small"
                                        checked={q.required}
                                        onChange={(e) => updateQuestion(q.id, 'required', e.target.checked)}
                                    />
                                }
                                label="Required"
                            />
                        </Box>
                        {q.includeImage && (
                            <Box sx={{ mb: 2 }}>
                                <input
                                    accept="image/*"
                                    type="file"
                                    onChange={(e) => {
                                        // Handle image upload here
                                        // For now, just log the file
                                        console.log('Image selected:', e.target.files[0]);
                                    }}
                                    style={{ display: 'none' }}
                                    id={`image-upload-${q.id}`}
                                />
                                <label htmlFor={`image-upload-${q.id}`}>
                                    <Button
                                        variant="outlined"
                                        component="span"
                                        startIcon={<UploadIcon />}
                                        size="small"
                                    >
                                        Upload Image
                                    </Button>
                                </label>
                            </Box>
                        )}
                    </Box>
                );

            default:
                return null;
        }
    };

    const handleSubmit = () => {
        const quizData = {
            settings,
            questions: questions.map(q => {
                const baseQuestion = {
                    type: q.type,
                    question: q.question,
                    randomize: q.randomize
                };

                switch (q.type) {
                    case 'MCQ':
                    case 'MSQ':
                        return {
                            ...baseQuestion,
                            options: q.options,
                            selectedAnswers: q.selectedAnswers
                        };
                    case 'SHORT_ANSWER':
                        return {
                            ...baseQuestion,
                            answer: q.answer,
                            maxLength: q.maxLength
                        };
                    case 'RADIO_GRID':
                    case 'CHECKBOX_GRID':
                        return {
                            ...baseQuestion,
                            rows: q.rows,
                            columns: q.columns,
                            answers: q.answers
                        };
                    default:
                        return baseQuestion;
                }
            })
        };

        console.log('Quiz submitted:', quizData);
        // Add your API call here
    };


    const validateQuestions = useMemo(() => {
        const messages = [];
        if (questions.length === 0) {
            messages.push("Please add at least one question");
            return { isValid: false, messages };
        }

        questions.forEach((q, index) => {
            const questionNumber = index + 1;

            // Check question text
            if (!q.question.trim()) {
                messages.push(`Question ${questionNumber}: Question text is required`);
            }

            switch (q.type) {
                case 'MCQ':
                case 'MSQ':
                    // Check options
                    q.options.forEach((opt, optIndex) => {
                        if (!opt.trim()) {
                            messages.push(`Question ${questionNumber}: Option ${optIndex + 1} is empty`);
                        }
                    });

                    // Check answer selection
                    if (q.selectedAnswers.length === 0) {
                        messages.push(`Question ${questionNumber}: Please select at least one correct answer`);
                    }
                    break;

                case 'SHORT_ANSWER':
                    if (!q.answer.trim()) {
                        messages.push(`Question ${questionNumber}: Model answer is required`);
                    }
                    break;

                case 'RADIO_GRID':
                case 'CHECKBOX_GRID':
                    // Check row labels
                    q.rows.forEach((row, rowIndex) => {
                        if (!row.trim()) {
                            messages.push(`Question ${questionNumber}: Row ${rowIndex + 1} label is empty`);
                        }
                    });

                    // Check column labels
                    q.columns.forEach((col, colIndex) => {
                        if (!col.trim()) {
                            messages.push(`Question ${questionNumber}: Column ${colIndex + 1} label is empty`);
                        }
                    });

                    // Check answers for each row
                    q.rows.forEach((_, rowIndex) => {
                        if (q.type === 'RADIO_GRID' && q.answers[rowIndex] === undefined) {
                            messages.push(`Question ${questionNumber}: Please select an answer for row ${rowIndex + 1}`);
                        } else if (q.type === 'CHECKBOX_GRID' && (!q.answers[rowIndex] || q.answers[rowIndex].length === 0)) {
                            messages.push(`Question ${questionNumber}: Please select at least one answer for row ${rowIndex + 1}`);
                        }
                    });
                    break;
                default:
                    return { isValid: false, message: "Form is not valid" }
            }
        });

        return { isValid: messages.length === 0, messages };
    }, [questions]);

    // Update validation state when questions change
    useEffect(() => {
        const { isValid: valid, messages } = validateQuestions;
        setIsValid(valid);
        setValidationMessages(messages);
    }, [validateQuestions]);

    const renderQuestionValidationMessages = (questionIndex) => {
        const questionMessages = validationMessages.filter(msg =>
            msg.startsWith(`Question ${questionIndex + 1}:`)
        );

        if (questionMessages.length === 0) return null;

        return (
            <Box sx={{ mt: 2, p: 2, bgcolor: '#fff3f3', borderRadius: 1 }}>
                {questionMessages.map((message, index) => (
                    <Typography
                        key={index}
                        color="error"
                        variant="body2"
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                    >
                        • {message.split(': ')[1]}
                    </Typography>
                ))}
            </Box>
        );
    };
    const renderGeneralValidationMessages = () => {
        const generalMessages = validationMessages.filter(msg =>
            !msg.startsWith('Question')
        );

        if (generalMessages.length === 0) return null;

        return (
            <Box sx={{ mb: 2, p: 2, bgcolor: '#fff3f3', borderRadius: 1 }}>
                {generalMessages.map((message, index) => (
                    <Typography
                        key={index}
                        color="error"
                        variant="body2"
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                    >
                        • {message}
                    </Typography>
                ))}
            </Box>
        );
    };
    return (
        <div id="ancestorContainer" className={Styles.ancestorContainer}>
            <Box sx={{ width: '100%', maxWidth: 1200, mx: 'auto', p: 3 }}>
                <Paper
                    elevation={3}
                    sx={{
                        position: 'sticky',
                        width: '100%',
                        top: 0,
                        zIndex: 1100,
                        backgroundColor: 'background.paper',
                    }}
                >
                    <Box sx={{
                        borderBottom: 1,
                        borderColor: 'divider',
                        px: 3,
                        pt: 2
                    }}>
                        <Tabs
                            value={activeTab}
                            onChange={handleTabChange}
                            aria-label="quiz form tabs"
                            centered
                        >
                            <Tab label="Questions" />
                            <Tab label="Settings" />
                            <Tab label="Responses" />
                        </Tabs>
                    </Box>
                </Paper>

                <Box sx={{ mt: 3 }}>
                    {activeTab === 0 && (
                        <Box>
                            <Card sx={{ m: 3, width: '100%' }}>
                                <CardContent>
                                    <Typography variant="h6">
                                        Assessment Details
                                    </Typography>
                                    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: '0.75em', margin: '1em 0' }}>
                                        <TextField
                                            fullWidth
                                            required
                                            label="Assessment Name"
                                            name="name"
                                            value={''}
                                            onChange={() => { }}
                                        />
                                        <TextField
                                            fullWidth
                                            required
                                            label="Assessment Description"
                                            name="description"
                                            value={''}
                                            onChange={() => { }}
                                            multiline
                                            rows={4}
                                        />
                                    </div>
                                </CardContent>
                            </Card>
                            {questions.map((q, index) => (
                                <Card key={q.id} sx={{ mb: 3 }}>
                                    <CardContent>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Typography variant="h6">
                                                    Question {index + 1}
                                                </Typography>
                                                <FormControl size="small" sx={{ width: 100 }}>
                                                    <TextField
                                                        label="Points"
                                                        type="number"
                                                        value={q.points}
                                                        onChange={(e) => handlePointsChange(q.id, e.target.value)}
                                                        size="small"
                                                        inputProps={{
                                                            input: {
                                                                min: 1,
                                                                max: 10,
                                                                step: 1
                                                            }
                                                        }}
                                                    />
                                                </FormControl>
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <FormControl size="small" sx={{ minWidth: 120 }}>
                                                    <InputLabel>Type</InputLabel>
                                                    <Select
                                                        value={q.type}
                                                        label="Type"
                                                        onChange={(e) => handleTypeChange(q.id, e.target.value)}
                                                    >
                                                        <MenuItem value="MCQ">MCQ</MenuItem>
                                                        <MenuItem value="MSQ">MSQ</MenuItem>
                                                        <MenuItem value="SHORT_ANSWER">Text</MenuItem>
                                                        <MenuItem value="RADIO_GRID">MCQ Grid</MenuItem>
                                                        <MenuItem value="CHECKBOX_GRID">MSQ Grid</MenuItem>
                                                    </Select>
                                                </FormControl>
                                                <IconButton
                                                    color="error"
                                                    onClick={() => deleteQuestion(q.id)}
                                                    size="small"
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Box>
                                        </Box>

                                        <TextField
                                            fullWidth
                                            label="Question"
                                            value={q.question}
                                            onChange={(e) => updateQuestion(q.id, 'question', e.target.value)}
                                            margin="normal"
                                        />

                                        {renderQuestionContent(q)}
                                        {renderQuestionValidationMessages(index)}
                                    </CardContent>
                                </Card>
                            ))}
                            {renderGeneralValidationMessages()}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', maxWidth: '1200px' }}>
                                <Button
                                    variant="contained"
                                    startIcon={<AddCircleOutlineIcon />}
                                    onClick={addQuestion}
                                    sx={{ mb: 3, width: '120px', height: '40px' }}
                                >
                                    Question
                                </Button>
                                <Button
                                    variant="contained"
                                    color="success"
                                    startIcon={<PublishIcon />}
                                    onClick={handleSubmit}
                                    disabled={!isValid}
                                    sx={{ mb: 3, width: '120px', height: '40px' }}
                                >
                                    Submit
                                </Button>
                            </div>
                        </Box>
                    )}

                    {activeTab === 1 && (
                        <Card>
                            <CardContent>
                                <Typography variant="h6">Quiz Settings</Typography>
                                <Divider />
                                <Grid container spacing={2} alignItems="center" sx={{ mt: 2 }}>
                                    <Grid item xs={8}>
                                        <Typography>Re-invite Students</Typography>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Button variant="outlined" size="small" component="span" startIcon={<GetAppIcon />} onClick={() => { console.log('Reinvited') }}>Re-invite</Button>
                                    </Grid>
                                    <Grid item xs={8}>
                                        <Typography>Make this a timed quiz</Typography>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Switch checked={settings.timedQuiz} onChange={(e) => handleSettingChange('timedQuiz', e.target.checked)} />
                                    </Grid>
                                    {settings.timedQuiz && (
                                        <>
                                            <Grid item xs={8}>
                                                <Typography>Time Limit (HH:mm:ss)</Typography>
                                            </Grid>
                                            <Grid item xs={4}>
                                                <TextField fullWidth size="small" value={settings.timeLimit} error={!!timeLimitError} helperText={timeLimitError} placeholder='HH:mm:ss' onChange={(e) => handleSettingChange('timeLimit', e.target.value)} />
                                            </Grid>
                                        </>
                                    )}
                                    <Grid item xs={8}>
                                        <Typography>Shuffle Question Order</Typography>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Switch checked={settings.shuffleQuestions} onChange={(e) => handleSettingChange('shuffleQuestions', e.target.checked)} />
                                    </Grid>
                                    <Grid item xs={8}>
                                        <Typography>Default Points per Question</Typography>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <TextField fullWidth size="small" type="number" value={settings.defaultPoints} onChange={(e) => handleSettingChange('defaultPoints', Math.max(1, Math.min(10, Number(e.target.value))))} />
                                    </Grid>
                                    <Grid item xs={8}>
                                        <Typography>Number of Attempts</Typography>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <TextField fullWidth size="small" type="number" value={settings.attempts} helperText={"Max attempts is 10"} onChange={(e) => handleSettingChange('attempts', Math.max(1, Math.min(10, Number(e.target.value))))} />
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    )}

                    {activeTab === 2 && (
                        <Card>
                            <CardContent>
                                <Typography variant="h6">Responses</Typography>
                                <Divider />
                                No responses yet
                            </CardContent>
                        </Card>
                    )}
                </Box>
            </Box>
        </div>
    );
};

